import React, { useState, useEffect, useRef, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  getSources, getKbStats, deleteSource, reingestSource, ingestFile, ingestLinks, ingestText,
  getCosts, getCaptures, promoteCapture, quarantineCapture,
  retagSource, setSourceSensitive as setSourceSensitiveApi,
  getBlindspots, createDocFromBlindspot,
  getAdmins, addAdmin, removeAdmin, setAdminNotifications,
  getProjectManagers, addProjectManager, removeProjectManager,
  draftBroadcast, createBroadcast, getBroadcasts, cancelBroadcast,
  retryBroadcast, sendBroadcastNow, getBroadcastRecipients, syncBroadcastRecipients,
} from "../api/guru";
import AccountMenu from "../components/AccountMenu";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import guruLogo from "../assets/guru-logo.png";

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_BADGE = {
  pdf:           "badge-pdf",
  docx:          "badge-docx",
  xlsx:          "badge-xlsx",
  gdoc:          "badge-gdoc",
  gsheet:        "badge-gsheet",
  gdrive_folder: "badge-gsheet",
  url:           "badge-url",
  text:          "badge-url",
};

const TYPE_LABEL = {
  pdf:           "PDF",
  docx:          "Word Doc",
  xlsx:          "Excel",
  gdoc:          "Google Doc",
  gsheet:        "Google Sheet",
  gdrive_folder: "Drive Folder",
  url:           "URL",
  text:          "Note",
};

const ACCEPTED_EXTENSIONS = [".pdf", ".docx", ".xlsx", ".xls", ".csv", ".txt"];

const TABS = [
  { id: "kb",         label: "📚 Knowledge Base" },
  { id: "captures",   label: "🧠 Captures"        },
  { id: "costs",      label: "💰 Costs"            },
  { id: "blindspots", label: "🔍 Blind Spots"      },
  { id: "admins",     label: "👤 Admins"           },
  { id: "pms",        label: "🧑‍💼 Project Managers" },
  { id: "broadcast",  label: "📢 Broadcast"        },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getFileTypeName(name) {
  const ext = name.split(".").pop().toLowerCase();
  if (ext === "pdf")  return "PDF";
  if (ext === "docx") return "Word document";
  if (ext === "xlsx" || ext === "xls") return "Excel spreadsheet";
  if (ext === "csv")  return "CSV file";
  if (ext === "txt")  return "Text file";
  return "file";
}

const fmt = {
  usd:      (v) => (v == null ? "—" : "$" + Number(v).toFixed(4)),
  usdMicro: (v) => (v == null ? "—" : "$" + Number(v).toFixed(6)),
  date:     (iso) => iso ? new Date(iso).toLocaleDateString()  : "—",
  datetime: (iso) => iso ? new Date(iso).toLocaleString()      : "—",
};

// ── Costs Panel ───────────────────────────────────────────────────────────────

function CostsPanel({ showAlert }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getCosts()
      .then(r => setData(r.data))
      .catch(() => showAlert("error", "Could not load cost data."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="panel-loading">Loading costs…</div>;
  if (!data)   return null;
  if (data.error) return <div className="alert alert-error">Backend error: {data.error}</div>;

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>Cost Dashboard <span className="panel-sub">last 7 days</span></h2>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <span style={{ fontSize: "12px", background: "#d1fae5", color: "#065f46", padding: "3px 10px", borderRadius: "12px", fontWeight: 600 }}>📊 Guru tracking</span>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <div className="stat-num">{data.total_queries_last_7d ?? 0}</div>
          <div className="stat-label">Guru Queries (7d)</div>
        </div>
        <div className="stat-box">
          <div className="stat-num">{fmt.usd(data.total_cost_usd_7d)}</div>
          <div className="stat-label">Total Spend (7d)</div>
        </div>
        <div className="stat-box">
          <div className="stat-num">{data.avg_cost_usd > 0 ? fmt.usdMicro(data.avg_cost_usd) : "—"}</div>
          <div className="stat-label">Avg / Query</div>
        </div>
        <div className="stat-box">
          <div className="stat-num">{data.p95_cost_usd > 0 ? fmt.usdMicro(data.p95_cost_usd) : "—"}</div>
          <div className="stat-label">p95 / Query</div>
        </div>
      </div>

      <div className="sources-section">
        <h3 style={{ marginBottom: "12px" }}>Daily Breakdown</h3>
        {(!data.daily || data.daily.length === 0) ? (
          <div className="empty-sources">No cost data yet.</div>
        ) : (
          <div className="table-scroll">
          <table className="sources-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Queries</th>
                <th>Total Cost</th>
                <th>Avg Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.daily.map((d, i) => (
                <tr key={i} style={i === 0 ? { background: "#f0fdf4" } : {}}>
                  <td style={{ fontWeight: i === 0 ? 600 : 400 }}>
                    {d.day}{i === 0 ? " 🟢 today" : ""}
                  </td>
                  <td>{d.queries != null ? d.queries : <span style={{ color: "#9ca3af" }}>—</span>}</td>
                  <td>{d.total_usd > 0 ? fmt.usd(d.total_usd) : <span style={{ color: "#9ca3af" }}>$0.00</span>}</td>
                  <td style={{ color: "#6b7280", fontSize: "12px" }}>
                    {d.avg_usd != null && d.avg_usd > 0 ? fmt.usdMicro(d.avg_usd) : <span style={{ color: "#d1d5db" }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "16px" }}>
        Estimated from token counts · Input $3/1M · Output $15/1M · Cache write $3.75/1M · Cache read $0.30/1M
      </p>
    </div>
  );
}

// ── Captures Panel ────────────────────────────────────────────────────────────

function CapturesPanel({ showAlert }) {
  const [captures, setCaptures]   = useState([]);
  const [statusFilter, setFilter] = useState("pending");
  const [loading, setLoading]     = useState(true);
  const [actioning, setActioning] = useState({});
  const [editing, setEditing]     = useState({});

  const load = useCallback((status) => {
    setLoading(true);
    getCaptures(status)
      .then(r => setCaptures(r.data.captures || []))
      .catch(() => showAlert("error", "Could not load captures."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { load(statusFilter); }, [statusFilter, load]);

  const handlePromote = async (id) => {
    setActioning(a => ({ ...a, [id]: "promote" }));
    try {
      await promoteCapture(id, editing[id] || null);
      showAlert("success", "✅ Promoted to Knowledge Base.");
      load(statusFilter);
    } catch {
      showAlert("error", "Could not promote capture.");
    } finally {
      setActioning(a => ({ ...a, [id]: null }));
    }
  };

  const handleQuarantine = async (id) => {
    if (!window.confirm("Quarantine this capture? It will be excluded from retrieval.")) return;
    setActioning(a => ({ ...a, [id]: "quarantine" }));
    try {
      await quarantineCapture(id);
      showAlert("success", "Capture quarantined.");
      load(statusFilter);
    } catch {
      showAlert("error", "Could not quarantine capture.");
    } finally {
      setActioning(a => ({ ...a, [id]: null }));
    }
  };

  const toggleEdit = (id, currentText) => {
    setEditing(e => {
      if (e[id] !== undefined) { const { [id]: _, ...rest } = e; return rest; }
      return { ...e, [id]: currentText };
    });
  };

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>Knowledge Captures <span className="panel-sub">auto-saved from every Guru response</span></h2>
        <button className="btn btn-secondary btn-sm" onClick={() => load(statusFilter)}>↻ Refresh</button>
      </div>

      <div className="filter-tabs">
        {["pending", "promoted", "quarantined", "all"].map(s => (
          <button
            key={s}
            className={"filter-tab" + (statusFilter === s ? " active" : "")}
            onClick={() => setFilter(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="panel-loading">Loading captures…</div>
      ) : captures.length === 0 ? (
        <div className="empty-sources">
          No {statusFilter === "all" ? "" : statusFilter} captures yet.
          {statusFilter === "pending" && " Guru will auto-save captures as users ask questions."}
        </div>
      ) : (
        <div className="capture-list">
          {captures.map(cap => {
            const isActioning = !!actioning[cap.id];
            const isEditing   = editing[cap.id] !== undefined;
            return (
              <div key={cap.id} className="capture-card">
                <div className="capture-meta">
                  <span className={"capture-status status-" + cap.status}>{cap.status}</span>
                  <span className="capture-repeat">🔁 {cap.repeat_count}x asked</span>
                  <span className="capture-source">{cap.sources_used || "—"}</span>
                  <span className="capture-date">{fmt.date(cap.created_at)}</span>
                </div>
                <div className="capture-question">❓ {cap.question}</div>
                {isEditing ? (
                  <textarea
                    className="capture-edit-textarea"
                    value={editing[cap.id]}
                    onChange={e => setEditing(ed => ({ ...ed, [cap.id]: e.target.value }))}
                    rows={6}
                  />
                ) : (
                  <div className="capture-response">{cap.response}</div>
                )}
                {cap.status !== "quarantined" && (
                  <div className="capture-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handlePromote(cap.id)}
                      disabled={isActioning}
                    >
                      {actioning[cap.id] === "promote" ? "⏳ Promoting…" : "✅ Promote to KB"}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => toggleEdit(cap.id, cap.response)}
                      disabled={isActioning}
                    >
                      {isEditing ? "✕ Cancel Edit" : "✏️ Edit Answer"}
                    </button>
                    {cap.status === "pending" && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleQuarantine(cap.id)}
                        disabled={isActioning}
                      >
                        {actioning[cap.id] === "quarantine" ? "⏳…" : "🚫 Quarantine"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Blind Spots Panel ─────────────────────────────────────────────────────────

function BlindSpotsPanel({ showAlert }) {
  const [spots, setSpots]       = useState([]);
  const [days, setDays]         = useState(7);
  const [loading, setLoading]   = useState(true);
  const [docForms, setDocForms] = useState({});

  const load = useCallback((d) => {
    setLoading(true);
    getBlindspots(d, 20)
      .then(r => setSpots(r.data.blindspots || []))
      .catch(() => showAlert("error", "Could not load blind spots."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { load(days); }, [days, load]);

  const toggleDoc = (q) => {
    setDocForms(f => {
      if (f[q]) { const { [q]: _, ...rest } = f; return rest; }
      return { ...f, [q]: { answer: "Q: " + q + "\n\nA: " } };
    });
  };

  const handleSaveDoc = async (question) => {
    const form = docForms[question];
    if (!form || !form.answer.trim()) return;
    setDocForms(f => ({ ...f, [question]: { ...f[question], saving: true } }));
    try {
      await createDocFromBlindspot(question, form.answer.trim());
      showAlert("success", "✅ Knowledge doc saved and promoted to KB!");
      setDocForms(f => { const { [question]: _, ...rest } = f; return rest; });
      load(days);
    } catch {
      showAlert("error", "Could not save knowledge doc.");
      setDocForms(f => ({ ...f, [question]: { ...f[question], saving: false } }));
    }
  };

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>Blind Spots <span className="panel-sub">queries where Guru had no internal source</span></h2>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <span style={{ fontSize: "13px", color: "#6b7280" }}>Last</span>
          {[7, 14, 30].map(d => (
            <button
              key={d}
              className={"filter-tab" + (days === d ? " active" : "")}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={() => load(days)}>↻</button>
        </div>
      </div>

      {loading ? (
        <div className="panel-loading">Loading blind spots…</div>
      ) : spots.length === 0 ? (
        <div className="empty-sources">
          No blind spots in the last {days} days — Guru answered everything from internal sources. 🎉
        </div>
      ) : (
        <>
          <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>
            These questions fell back to general SEO knowledge because the KB had nothing relevant.
            Create a doc for the most common ones.
          </p>
          <div className="capture-list">
            {spots.map((spot, i) => {
              const form = docForms[spot.question];
              return (
                <div key={i} className="capture-card">
                  <div className="capture-meta">
                    <span className="capture-repeat">🔁 {spot.count}x asked</span>
                    <span className="capture-date">Last: {fmt.datetime(spot.asked_at)}</span>
                  </div>
                  <div className="capture-question">❓ {spot.question}</div>
                  <p className="capture-hint">{spot.create_doc_hint}</p>
                  {!form ? (
                    <button className="btn btn-secondary btn-sm" onClick={() => toggleDoc(spot.question)}>
                      📝 Create Knowledge Doc
                    </button>
                  ) : (
                    <div className="doc-form">
                      <label style={{ fontSize: "12px", color: "#374151", fontWeight: 600 }}>
                        Write the answer Guru should give:
                      </label>
                      <textarea
                        className="capture-edit-textarea"
                        value={form.answer}
                        onChange={e => setDocForms(f => ({
                          ...f, [spot.question]: { ...f[spot.question], answer: e.target.value },
                        }))}
                        rows={6}
                        disabled={form.saving}
                      />
                      <div className="capture-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleSaveDoc(spot.question)}
                          disabled={form.saving || !form.answer.trim()}
                        >
                          {form.saving ? "⏳ Saving…" : "💾 Save to KB"}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => toggleDoc(spot.question)}
                          disabled={form.saving}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── Admins Panel ──────────────────────────────────────────────────────────────

function AdminsPanel({ showAlert, currentUserEmail }) {
  const [admins, setAdmins]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding]     = useState(false);
  const [removing, setRemoving] = useState({});
  const [togglingNotif, setTogglingNotif] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getAdmins()
      .then(r => setAdmins(r.data.admins || []))
      .catch(() => showAlert("error", "Could not load admin list."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setAdding(true);
    try {
      await addAdmin(email);
      showAlert("success", "✅ " + email + " can now access the Admin Panel.");
      setNewEmail("");
      load();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Could not add admin.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm("Remove admin access for " + email + "?")) return;
    setRemoving(r => ({ ...r, [email]: true }));
    try {
      await removeAdmin(email);
      showAlert("success", email + " is no longer an admin.");
      load();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Could not remove admin.");
    } finally {
      setRemoving(r => ({ ...r, [email]: false }));
    }
  };

  const handleToggleNotifications = async (admin) => {
    const nextEnabled = !admin.email_notifications_enabled;
    setTogglingNotif(t => ({ ...t, [admin.email]: true }));
    // Optimistic update so the switch flips immediately.
    setAdmins(list => list.map(a => a.email === admin.email ? { ...a, email_notifications_enabled: nextEnabled } : a));
    try {
      await setAdminNotifications(admin.email, nextEnabled);
      showAlert("success", admin.email + (nextEnabled ? " will get" : " will stop getting") + " pending-access emails.");
    } catch (err) {
      // Revert on failure.
      setAdmins(list => list.map(a => a.email === admin.email ? { ...a, email_notifications_enabled: admin.email_notifications_enabled } : a));
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Could not update email notifications.");
    } finally {
      setTogglingNotif(t => ({ ...t, [admin.email]: false }));
    }
  };

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>Knowledge Admins <span className="panel-sub">who can access this Admin Panel</span></h2>
        <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      <form onSubmit={handleAdd} className="admin-inline-form" style={{ display: "flex", gap: "8px", marginBottom: "20px", maxWidth: "440px" }}>
        <input
          type="email"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          placeholder="name@88gravity.com"
          style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
        />
        <button className="btn btn-primary btn-sm admin-inline-form-btn" disabled={adding || !newEmail.trim()}>
          {adding ? "Adding…" : "+ Add Admin"}
        </button>
      </form>

      {loading ? (
        <div className="panel-loading">Loading admins…</div>
      ) : admins.length === 0 ? (
        <div className="empty-sources">No admins yet.</div>
      ) : (
        <div className="table-scroll">
        <table className="sources-table">
          <thead>
            <tr><th>Email</th><th>Added</th><th>Source</th><th>Pending-access emails</th><th></th></tr>
          </thead>
          <tbody>
            {admins.map(a => (
              <tr key={a.email}>
                <td>
                  {a.email}
                  {a.email === currentUserEmail && (
                    <span style={{ marginLeft: 6, fontSize: "11px", color: "#9ca3af" }}>(you)</span>
                  )}
                </td>
                <td style={{ color: "#6b7280", fontSize: "12px" }}>
                  {a.added_at ? new Date(a.added_at).toLocaleDateString() : <span style={{ color: "#d1d5db" }}>—</span>}
                </td>
                <td><span className="badge" style={{ fontSize: "11px" }}>{a.source === "env" ? "via .env" : "added in app"}</span></td>
                <td>
                  {a.source === "env" ? (
                    <span
                      style={{ fontSize: "11px", color: "#9ca3af" }}
                      title="Env-listed admins always get emails — edit .env to change this"
                    >
                      always on (via .env)
                    </span>
                  ) : (
                    <label
                      className={"toggle-switch" + (togglingNotif[a.email] ? " toggle-switch-disabled" : "")}
                      title={a.email_notifications_enabled ? "Turn off pending-access emails" : "Turn on pending-access emails"}
                    >
                      <input
                        type="checkbox"
                        checked={!!a.email_notifications_enabled}
                        disabled={!!togglingNotif[a.email]}
                        onChange={() => handleToggleNotifications(a)}
                      />
                      <span className="toggle-switch-slider" />
                    </label>
                  )}
                </td>
                <td>
                  {a.source === "env" ? (
                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>remove via .env</span>
                  ) : (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemove(a.email)}
                      disabled={!!removing[a.email] || a.email === currentUserEmail}
                      title={a.email === currentUserEmail ? "You can't remove your own access" : "Remove admin access"}
                    >
                      {removing[a.email] ? "⏳" : "Remove"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "16px" }}>
        Knowledge Admins get this Admin Panel (Knowledge Base, Captures, Costs, Blind Spots). Everyone
        else signed in with an @88gravity.com account is a Standard User and only sees Chat.
        The "Pending-access emails" switch controls only the nightly-job / registration digest —
        it doesn't affect Admin Panel access. Project managers' own per-project emails are separate
        and unaffected by this switch.
      </p>
    </div>
  );
}

// ── Project Managers Panel (Dev, 2026-07-03) ────────────────────────────────
// Same shape as AdminsPanel above, minus the env-allowlist half — every
// Project Manager is UI-added, so there's no "via .env" source/badge, no
// notifications toggle, and no "can't remove yourself" guard tied to a
// fixed allowlist.

function ProjectManagersPanel({ showAlert, currentUserEmail }) {
  const [pms, setPms]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [newName, setNewName]   = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding]     = useState(false);
  const [removing, setRemoving] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getProjectManagers()
      .then(r => setPms(r.data.project_managers || []))
      .catch(() => showAlert("error", "Could not load project manager list."))
      .finally(() => setLoading(false));
  }, [showAlert]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    const email = newEmail.trim().toLowerCase();
    if (!name || !email) return;
    setAdding(true);
    try {
      await addProjectManager(email, name);
      showAlert("success", "✅ " + name + " (" + email + ") can now sign in as a Project Manager.");
      setNewName("");
      setNewEmail("");
      load();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Could not add project manager.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm("Remove Project Manager access for " + email + "?")) return;
    setRemoving(r => ({ ...r, [email]: true }));
    try {
      await removeProjectManager(email);
      showAlert("success", email + " is no longer a Project Manager.");
      load();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Could not remove project manager.");
    } finally {
      setRemoving(r => ({ ...r, [email]: false }));
    }
  };

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>Project Managers <span className="panel-sub">who can access the Projects panel</span></h2>
        <button className="btn btn-secondary btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      <form onSubmit={handleAdd} className="admin-inline-form" style={{ display: "flex", gap: "8px", marginBottom: "20px", maxWidth: "540px" }}>
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="Full name"
          style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
        />
        <input
          type="email"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          placeholder="name@88gravity.com"
          style={{ flex: 1, padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
        />
        <button className="btn btn-primary btn-sm admin-inline-form-btn" disabled={adding || !newName.trim() || !newEmail.trim()}>
          {adding ? "Adding…" : "+ Add Project Manager"}
        </button>
      </form>

      {loading ? (
        <div className="panel-loading">Loading project managers…</div>
      ) : pms.length === 0 ? (
        <div className="empty-sources">No project managers yet.</div>
      ) : (
        <div className="table-scroll">
        <table className="sources-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Added</th><th></th></tr>
          </thead>
          <tbody>
            {pms.map(p => (
              <tr key={p.email}>
                <td>{p.name}</td>
                <td>
                  {p.email}
                  {p.email === currentUserEmail && (
                    <span style={{ marginLeft: 6, fontSize: "11px", color: "#9ca3af" }}>(you)</span>
                  )}
                </td>
                <td style={{ color: "#6b7280", fontSize: "12px" }}>
                  {p.added_at ? new Date(p.added_at).toLocaleDateString() : <span style={{ color: "#d1d5db" }}>—</span>}
                </td>
                <td>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemove(p.email)}
                    disabled={!!removing[p.email]}
                  >
                    {removing[p.email] ? "⏳" : "Remove"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      <p style={{ fontSize: "12px", color: "#9ca3af", marginTop: "16px" }}>
        Project Managers get the Chat and Projects panels only — never this Admin Panel. They're
        subject to the same daily chat question quota as a Standard User. Only a Knowledge Admin can
        add or remove a Project Manager; once added, they'll appear in the manager dropdown when
        registering or editing a project.
      </p>
    </div>
  );
}

// ── Broadcast Panel ───────────────────────────────────────────────────────────

function BroadcastPanel({ showAlert }) {
  // ── Compose state ──
  const [subject, setSubject]           = useState("");
  const [instructions, setInstructions] = useState("");
  // recipient mode: "all" | "specific" | "custom"
  const [recipientMode, setRecipientMode] = useState("all");
  const [allRecipients, setAllRecipients] = useState([]);  // from directory
  const [selectedEmails, setSelectedEmails] = useState([]); // for "specific"
  const [customEmails, setCustomEmails]   = useState("");   // for "custom"
  const [recipientSearch, setRecipientSearch] = useState("");

  // ── Draft state ──
  const [draft, setDraft]         = useState(null);   // { subject, body_text, body_html }
  const [editedBody, setEditedBody] = useState("");
  const [editMode, setEditMode]   = useState(false);
  const [drafting, setDrafting]   = useState(false);

  // ── Schedule state ──
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleAt, setScheduleAt]     = useState("");

  // ── History state ──
  const [history, setHistory]         = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [sending, setSending]         = useState(false);

  // Load recipients + history on mount
  useEffect(() => {
    getBroadcastRecipients(false)
      .then(r => setAllRecipients(r.data.recipients || []))
      .catch(() => {});
    loadHistory();
  }, []); // eslint-disable-line

  const loadHistory = () => {
    setHistoryLoading(true);
    getBroadcasts('', 20)
      .then(r => setHistory(r.data.broadcasts || []))
      .catch(() => showAlert("error", "Could not load broadcast history."))
      .finally(() => setHistoryLoading(false));
  };

  // Resolve final recipient list based on mode
  const resolveRecipients = () => {
    if (recipientMode === "all") return null; // backend uses full directory
    if (recipientMode === "specific") return selectedEmails;
    if (recipientMode === "custom") {
      return customEmails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    }
    return null;
  };

  const recipientCount = () => {
    if (recipientMode === "all") return allRecipients.length || "all";
    if (recipientMode === "specific") return selectedEmails.length;
    if (recipientMode === "custom") {
      return customEmails.split(/[\n,;]+/).filter(e => e.trim()).length;
    }
    return 0;
  };

  const toggleSpecific = (email) => {
    setSelectedEmails(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const handleGenerateDraft = async () => {
    if (!subject.trim() || !instructions.trim()) {
      showAlert("error", "Please fill in subject and instructions before generating.");
      return;
    }
    setDrafting(true);
    setDraft(null);
    try {
      const r = await draftBroadcast(subject, instructions);
      setDraft(r.data);
      setEditedBody(r.data.body_text);
      setEditMode(false);
    } catch {
      showAlert("error", "AI draft generation failed. Please try again.");
    } finally {
      setDrafting(false);
    }
  };

  const handleSend = async () => {
    if (!draft) return;
    const recipients = resolveRecipients();
    if (recipientMode !== "all" && (!recipients || recipients.length === 0)) {
      showAlert("error", "Please select at least one recipient.");
      return;
    }
    setSending(true);
    try {
      const finalBody = editMode ? editedBody : draft.body_text;
      const scheduledAt = scheduleMode && scheduleAt ? new Date(scheduleAt).toISOString() : null;
      await createBroadcast(draft.subject, finalBody, draft.body_html, scheduledAt, recipients);
      showAlert("success", scheduledAt
        ? `Broadcast scheduled for ${new Date(scheduleAt).toLocaleString()}`
        : `Broadcast sent to ${recipientCount()} recipient(s)!`
      );
      // Reset form
      setSubject(""); setInstructions(""); setDraft(null); setEditedBody("");
      setSelectedEmails([]); setCustomEmails(""); setScheduleAt(""); setScheduleMode(false);
      setRecipientMode("all");
      loadHistory();
    } catch {
      showAlert("error", "Failed to send broadcast. Check SMTP config.");
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await cancelBroadcast(id);
      showAlert("success", "Broadcast cancelled.");
      loadHistory();
    } catch {
      showAlert("error", "Could not cancel broadcast.");
    }
  };

  const handleRetry = async (id) => {
    try {
      await retryBroadcast(id);
      showAlert("success", "Broadcast queued for retry.");
      loadHistory();
    } catch {
      showAlert("error", "Could not retry broadcast.");
    }
  };

  const handleSyncRecipients = async () => {
    try {
      const r = await syncBroadcastRecipients();
      showAlert("success", `Synced ${r.data.synced_count} recipients from Google Workspace.`);
      const r2 = await getBroadcastRecipients(false);
      setAllRecipients(r2.data.recipients || []);
    } catch {
      showAlert("error", "Recipient sync failed.");
    }
  };

  const filteredRecipients = allRecipients.filter(r =>
    !recipientSearch || r.email.includes(recipientSearch) || (r.name || "").toLowerCase().includes(recipientSearch.toLowerCase())
  );

  const statusBadge = (s) => {
    const map = { sent: "#d1fae5|#065f46", scheduled: "#dbeafe|#1e40af", sending: "#fef3c7|#92400e", failed: "#fee2e2|#991b1b", cancelled: "#f3f4f6|#6b7280" };
    const [bg, color] = (map[s] || "#f3f4f6|#6b7280").split("|");
    return <span style={{ background: bg, color, padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 600 }}>{s}</span>;
  };

  const fmtDt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

  return (
    <div className="admin-panel">
      <div className="panel-header">
        <h2>📢 Broadcast Email</h2>
        <button className="btn btn-secondary btn-sm" onClick={handleSyncRecipients}>
          🔄 Sync Recipients
        </button>
      </div>
      <p style={{ color: "#6b7280", fontSize: "14px", marginBottom: "20px" }}>
        Compose an AI-drafted email and send it to employees. Only admins can use this.
      </p>

      {/* ── Step 1: Recipients ── */}
      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "16px", marginBottom: "16px" }}>
        <h3 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: 700 }}>1. Who to send to?</h3>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "12px" }}>
          {[
            { id: "all",      label: `👥 All Employees (${allRecipients.length || "?"})`, desc: "Everyone in Google Workspace" },
            { id: "specific", label: "🔍 Specific People",                                 desc: "Pick individuals from the directory" },
            { id: "custom",   label: "✉️ Custom Emails",                                    desc: "Type any email addresses" },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setRecipientMode(opt.id)}
              style={{
                padding: "10px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600,
                border: recipientMode === opt.id ? "2px solid #4f46e5" : "1px solid #d1d5db",
                background: recipientMode === opt.id ? "#eef2ff" : "#fff",
                color: recipientMode === opt.id ? "#4f46e5" : "#374151",
              }}
            >
              {opt.label}
              <div style={{ fontSize: "11px", fontWeight: 400, color: "#6b7280", marginTop: "2px" }}>{opt.desc}</div>
            </button>
          ))}
        </div>

        {recipientMode === "specific" && (
          <div>
            <input
              placeholder="Search by name or email…"
              value={recipientSearch}
              onChange={e => setRecipientSearch(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "8px", fontSize: "13px" }}
            />
            <div style={{ maxHeight: "180px", overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: "6px", background: "#fff" }}>
              {filteredRecipients.length === 0
                ? <div style={{ padding: "12px", color: "#9ca3af", fontSize: "13px" }}>No recipients found. Click Sync Recipients.</div>
                : filteredRecipients.map(r => (
                  <label key={r.email} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid #f3f4f6" }}>
                    <input type="checkbox" checked={selectedEmails.includes(r.email)} onChange={() => toggleSpecific(r.email)} />
                    <span style={{ fontSize: "13px" }}>{r.name || r.email} <span style={{ color: "#9ca3af", fontSize: "11px" }}>{r.name ? `(${r.email})` : ""}</span></span>
                  </label>
                ))
              }
            </div>
            {selectedEmails.length > 0 && (
              <div style={{ marginTop: "6px", fontSize: "12px", color: "#4f46e5" }}>✓ {selectedEmails.length} selected</div>
            )}
          </div>
        )}

        {recipientMode === "custom" && (
          <textarea
            placeholder={"Enter email addresses, one per line or comma-separated:\njohn@example.com\njane@example.com"}
            value={customEmails}
            onChange={e => setCustomEmails(e.target.value)}
            rows={4}
            style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px", resize: "vertical", boxSizing: "border-box" }}
          />
        )}
      </div>

      {/* ── Step 2: Compose ── */}
      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "16px", marginBottom: "16px" }}>
        <h3 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: 700 }}>2. Compose</h3>
        <input
          placeholder="Email subject…"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #d1d5db", marginBottom: "10px", fontSize: "13px", boxSizing: "border-box" }}
        />
        <textarea
          placeholder={"Describe what you want to communicate. Example:\n- Office will be closed on Friday Aug 1\n- New leave policy starts next month\n- Remind team to submit timesheets by Thursday"}
          value={instructions}
          onChange={e => setInstructions(e.target.value)}
          rows={5}
          style={{ width: "100%", padding: "10px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px", resize: "vertical", boxSizing: "border-box", marginBottom: "12px" }}
        />
        <button
          className="btn btn-primary"
          onClick={handleGenerateDraft}
          disabled={drafting || !subject.trim() || !instructions.trim()}
          style={{ opacity: drafting ? 0.7 : 1 }}
        >
          {drafting ? "✨ Generating draft…" : "✨ Generate AI Draft"}
        </button>
      </div>

      {/* ── Step 3: Preview & Confirm ── */}
      {draft && (
        <div style={{ background: "#fffbeb", border: "2px solid #f59e0b", borderRadius: "10px", padding: "20px", marginBottom: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
            <h3 style={{ fontSize: "14px", fontWeight: 700, margin: 0 }}>3. Preview — is this what you want to send?</h3>
            <span style={{ fontSize: "12px", color: "#6b7280" }}>To: {recipientCount()} recipient(s)</span>
          </div>

          <div style={{ background: "#fff", border: "1px solid #fcd34d", borderRadius: "8px", padding: "16px", marginBottom: "14px" }}>
            <div style={{ marginBottom: "8px", fontSize: "13px" }}>
              <strong>Subject:</strong> {draft.subject}
            </div>
            <hr style={{ border: "none", borderTop: "1px solid #fde68a", margin: "10px 0" }} />
            {editMode ? (
              <textarea
                value={editedBody}
                onChange={e => setEditedBody(e.target.value)}
                rows={12}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px", resize: "vertical", boxSizing: "border-box" }}
              />
            ) : (
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: "13px", fontFamily: "inherit", lineHeight: 1.6 }}>
                {editedBody}
              </pre>
            )}
          </div>

          {/* Schedule toggle */}
          <div style={{ marginBottom: "14px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
              <input type="checkbox" checked={scheduleMode} onChange={e => setScheduleMode(e.target.checked)} />
              Schedule for later
            </label>
            {scheduleMode && (
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={e => setScheduleAt(e.target.value)}
                style={{ marginTop: "8px", padding: "8px 12px", borderRadius: "6px", border: "1px solid #d1d5db", fontSize: "13px" }}
              />
            )}
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              className="btn btn-primary"
              onClick={handleSend}
              disabled={sending || (scheduleMode && !scheduleAt)}
            >
              {sending ? "Sending…" : scheduleMode ? "🕐 Schedule" : "✅ Send Now"}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? "👁 View" : "✏️ Edit"}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleGenerateDraft}
              disabled={drafting}
            >
              🔄 Regenerate
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => { setDraft(null); setEditedBody(""); }}
            >
              ✕ Discard
            </button>
          </div>
        </div>
      )}

      {/* ── History ── */}
      <div style={{ marginTop: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 700 }}>Broadcast History</h3>
          <button className="btn btn-secondary btn-sm" onClick={loadHistory}>↻ Refresh</button>
        </div>
        {historyLoading ? (
          <div className="panel-loading">Loading history…</div>
        ) : history.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: "13px", padding: "20px", textAlign: "center" }}>No broadcasts yet.</div>
        ) : (
          <div className="table-scroll">
            <table className="sources-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Recipients</th>
                  <th>Scheduled / Sent</th>
                  <th>Created by</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map(b => (
                  <tr key={b.id}>
                    <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={b.subject}>{b.subject}</td>
                    <td>{statusBadge(b.status)}</td>
                    <td>{b.recipient_count || "—"}</td>
                    <td style={{ fontSize: "12px", color: "#6b7280" }}>{fmtDt(b.sent_at || b.scheduled_at)}</td>
                    <td style={{ fontSize: "12px", color: "#6b7280" }}>{b.created_by}</td>
                    <td>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {b.status === "scheduled" && (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleCancel(b.id)}>Cancel</button>
                        )}
                        {b.status === "failed" && (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleRetry(b.id)}>Retry</button>
                        )}
                        {b.error_detail && (
                          <span title={b.error_detail} style={{ cursor: "help", color: "#ef4444", fontSize: "12px" }}>⚠️</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Admin Component ──────────────────────────────────────────────────────

export default function Admin() {
  const { user } = useAuth();
  const { darkTheme, setDarkTheme } = useChat();
  const [activeTab, setActiveTab] = useState("kb");
  const [alert, setAlert]         = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile/tablet off-canvas drawer (Dev, responsive pass)

  // KB state
  const [sources, setSources]         = useState([]);
  const [stats, setStats]             = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [reingesting, setReingesting] = useState({});
  const [dragOver, setDragOver]       = useState(false);
  const fileInput                     = useRef(null);
  const [urlsText, setUrlsText]       = useState("");
  const [globalLabel, setGlobalLabel] = useState("");
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [batchResults, setBatchResults] = useState(null);

  const showAlert = useCallback((type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 6000);
  }, []);

  const fetchKbData = useCallback(async () => {
    try {
      const [srcRes, statsRes] = await Promise.all([getSources(), getKbStats()]);
      setSources(srcRes.data.sources || []);
      setStats(statsRes.data);
    } catch {
      showAlert("error", "Could not load knowledge base data. Is the backend running?");
    }
  }, [showAlert]);

  useEffect(() => { fetchKbData(); }, [fetchKbData]);

  const handleFile = async (file) => {
    if (!file) return;
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      showAlert("error", "Unsupported file type " + JSON.stringify(ext) + ". Please upload a PDF, Word (.docx), Excel (.xlsx), CSV, or Text (.txt) file.");
      return;
    }
    setLoadingFile(true);
    try {
      const { data } = await ingestFile(file);
      showAlert("success", "✅ " + JSON.stringify(file.name) + " ingested — " + data.chunks_added + " chunks added.");
      fetchKbData();
    } catch (err) {
      // Full diagnostic dump -- open the browser console (F12) on the live
      // site when an upload fails to see the real cause: status code, the
      // backend's error detail (if the request even reached the backend),
      // or a network-level failure (CORS block, timeout, connection refused,
      // 413 from a reverse-proxy body-size limit before FastAPI ever saw it).
      console.error("[KB upload] failed:", {
        file: file.name,
        size: file.size,
        status: err.response ? err.response.status : "no response (network/CORS/timeout)",
        detail: err.response && err.response.data,
        message: err.message,
        code: err.code,
      });
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Failed to ingest " + getFileTypeName(file.name) + ".");
    } finally {
      setLoadingFile(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const handleLinks = async (e) => {
    e.preventDefault();
    setBatchResults(null);
    const lines = urlsText.split("\n").map(l => l.trim()).filter(l => l && !l.startsWith("#"));
    if (lines.length === 0) { showAlert("error", "Please enter at least one URL."); return; }
    if (lines.length > 20)  { showAlert("error", "Maximum 20 URLs per batch."); return; }
    const links = lines.map((url, i) => ({ url, label: globalLabel.trim() ? globalLabel.trim() + " (" + (i + 1) + ")" : "" }));
    setLoadingLinks(true);
    try {
      const { data } = await ingestLinks(links);
      setBatchResults(data);
      const s = data.summary;
      if (s.failed === 0) showAlert("success", "✅ All " + s.total + " URLs ingested — " + s.total_chunks_added + " chunks added.");
      else showAlert("error", "⚠️ " + s.success + "/" + s.total + " ingested. " + s.failed + " failed — see details below.");
      setUrlsText("");
      setGlobalLabel("");
      fetchKbData();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Batch ingestion failed.");
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleReingest = async (id, label) => {
    setReingesting(r => ({ ...r, [id]: true }));
    try {
      const { data } = await reingestSource(id);
      showAlert("success", "🔄 " + JSON.stringify(label) + " refreshed — " + data.chunks_upserted + " chunks synced, " + data.chunks_deleted + " stale removed.");
      fetchKbData();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Re-ingest failed.");
    } finally {
      setReingesting(r => ({ ...r, [id]: false }));
    }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm("Remove " + JSON.stringify(label) + " from the knowledge base?")) return;
    try {
      await deleteSource(id);
      showAlert("success", JSON.stringify(label) + " removed.");
      fetchKbData();
    } catch {
      showAlert("error", "Could not remove source.");
    }
  };

  const urlCount = urlsText.split("\n").filter(l => l.trim() && !l.trim().startsWith("#")).length;

  // Note ingest state
  const [noteText,    setNoteText]    = useState("");
  const [noteLabel,   setNoteLabel]   = useState("");
  const [savingNote,  setSavingNote]  = useState(false);

  // Retag + sensitive state (source-level)
  const [retagOpen,    setRetagOpen]    = useState({});
  const [retagForm,    setRetagForm]    = useState({});
  const [retagging,    setRetagging2]   = useState({});
  const [srcSensitive, setSrcSensitive] = useState({});
  const [sensitiving,  setSensitiving]  = useState({});

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) { showAlert("error", "Note cannot be empty."); return; }
    setSavingNote(true);
    try {
      const { data } = await ingestText(noteText.trim(), noteLabel.trim() || "Manual Note");
      showAlert("success", "✅ Note ingested — " + data.chunks_added + " chunks added.");
      setNoteText("");
      setNoteLabel("");
      fetchKbData();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Failed to save note.");
    } finally {
      setSavingNote(false);
    }
  };

  const handleRetagToggle = (id, label) => {
    setRetagOpen(r => ({ ...r, [id]: !r[id] }));
    setRetagForm(f => ({ ...f, [id]: f[id] || { label: label || "", category: "" } }));
  };

  const handleRetagSave = async (id) => {
    const form = retagForm[id] || {};
    if (!form.label && !form.category) { showAlert("error", "Enter a label or category to retag."); return; }
    setRetagging2(r => ({ ...r, [id]: true }));
    try {
      await retagSource(id, form.label || null, form.category || null);
      showAlert("success", "🏷️ Source retagged.");
      setRetagOpen(r => ({ ...r, [id]: false }));
      fetchKbData();
    } catch (err) {
      showAlert("error", (err.response && err.response.data && err.response.data.detail) || "Retag failed.");
    } finally {
      setRetagging2(r => ({ ...r, [id]: false }));
    }
  };

  const handleSensitiveToggle = async (id) => {
    const next = !srcSensitive[id];
    setSensitiving(s => ({ ...s, [id]: true }));
    try {
      await setSourceSensitiveApi(id, next);
      setSrcSensitive(s => ({ ...s, [id]: next }));
      showAlert("success", next ? "🔒 Source hidden from Guru." : "👁️ Source visible to Guru.");
    } catch (err) {
      showAlert("error", "Could not update sensitivity.");
    } finally {
      setSensitiving(s => ({ ...s, [id]: false }));
    }
  };

  return (
    <div className="chat-page">
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`gpt-sidebar${sidebarOpen ? ' gpt-sidebar-open' : ''}`}>
        <div className="gpt-sidebar-brand">
          <img src={guruLogo} alt="Guru" className="gpt-brand-logo" />
          <div>
            <div className="gpt-brand-name">Guru</div>
            <div className="gpt-brand-sub">SEO Knowledge Agent</div>
          </div>
        </div>
        <div className="gpt-sidebar-topnav">
          <NavLink to="/chat"          className="gpt-nav-link"><span>💬</span> Back to Chat</NavLink>
          <NavLink to="/admin"         className="gpt-nav-link active-nav"><span>⚙️</span> Admin</NavLink>
          <NavLink to="/projects"      className="gpt-nav-link"><span>📋</span> Projects</NavLink>
          <NavLink to="/notifications" className="gpt-nav-link"><span>📣</span> Notifications</NavLink>
          <NavLink to="/query-history" className="gpt-nav-link"><span>🔍</span> Query History</NavLink>
        </div>

        <div className="gpt-sidebar-spacer" />
        <div style={{ padding: '8px 16px', flexShrink: 0 }}>
          <button className="theme-toggle-btn" style={{ width: '100%', justifyContent: 'flex-start', gap: '8px' }} onClick={() => setDarkTheme(d => !d)} title={darkTheme ? 'Switch to light theme' : 'Switch to dark theme'}>
            {darkTheme ? '☀️ Light mode' : '🌙 Dark mode'}
          </button>
        </div>
        <AccountMenu style={{ marginTop: '0' }} />
      </div>

      <div className="admin-page">
        <div className="mobile-page-header">
          <button
            type="button"
            className="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >☰</button>
          <h1>Admin Panel</h1>
        </div>
        <p className="subtitle">Knowledge Base · Capture Review · Cost Dashboard · Blind Spot Tracker</p>

        {alert && <div className={"alert alert-" + alert.type}>{alert.msg}</div>}

        <div className="admin-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={"admin-tab" + (activeTab === t.id ? " active" : "")}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "costs"      && <CostsPanel showAlert={showAlert} />}
        {activeTab === "captures"   && <CapturesPanel showAlert={showAlert} />}
        {activeTab === "blindspots" && <BlindSpotsPanel showAlert={showAlert} />}
        {activeTab === "admins"     && <AdminsPanel showAlert={showAlert} currentUserEmail={user?.email} />}
        {activeTab === "pms"        && <ProjectManagersPanel showAlert={showAlert} currentUserEmail={user?.email} />}
        {activeTab === "broadcast"  && <BroadcastPanel showAlert={showAlert} />}

        {activeTab === "kb" && (
          <div className="admin-panel">
            <div className="panel-header"><h2>Knowledge Base</h2></div>
            <p style={{ color: "#6b7280", marginBottom: "16px", fontSize: "14px" }}>
              Add PDFs, Word docs, Excel sheets, Google Docs/Sheets, or any URL. Guru uses them to answer your questions.
            </p>

            {stats && (
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-num">{stats.total_sources}</div>
                  <div className="stat-label">Sources</div>
                </div>
                <div className="stat-box">
                  <div className="stat-num">{stats.total_chunks}</div>
                  <div className="stat-label">Indexed Chunks</div>
                </div>
                <div className="stat-box">
                  <div className="stat-num">
                    {(stats.sources_by_type && stats.sources_by_type.pdf || 0) +
                     (stats.sources_by_type && stats.sources_by_type.docx || 0) +
                     (stats.sources_by_type && stats.sources_by_type.xlsx || 0)}
                  </div>
                  <div className="stat-label">Files</div>
                </div>
                <div className="stat-box">
                  <div className="stat-num">
                    {(stats.sources_by_type && stats.sources_by_type.gdoc || 0) +
                     (stats.sources_by_type && stats.sources_by_type.gsheet || 0) +
                     (stats.sources_by_type && stats.sources_by_type.gdrive_folder || 0) +
                     (stats.sources_by_type && stats.sources_by_type.url || 0)}
                  </div>
                  <div className="stat-label">Links</div>
                </div>
              </div>
            )}

            <div className="admin-grid">
              <div className="admin-card">
                <h3><span>📁</span> Upload File</h3>
                <div
                  className={"drop-zone" + (dragOver ? " drag-over" : "")}
                  onClick={() => fileInput.current && fileInput.current.click()}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  <div className="drop-icon">📂</div>
                  {loadingFile ? (
                    <p>Ingesting…</p>
                  ) : (
                    <>
                      <p>Click or drag a file here</p>
                      <p style={{ fontSize: "11px", marginTop: "4px", color: "#6b7280" }}>PDF · Word (.docx) · Excel (.xlsx) · CSV · Text (.txt)</p>
                      <p style={{ fontSize: "11px", marginTop: "2px", color: "#9ca3af" }}>SOPs, playbooks, reports, keyword sheets</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS.join(",")}
                  style={{ display: "none" }}
                  onChange={e => handleFile(e.target.files[0])}
                />
              </div>

              <div className="admin-card">
                <h3><span>🔗</span> Add URLs <span style={{ fontSize: "12px", fontWeight: 400, color: "#6b7280" }}>(Google Docs, Sheets, or any website)</span></h3>
                <form onSubmit={handleLinks}>
                  <div className="form-group">
                    <label>
                      URLs — one per line
                      {urlCount > 0 && <span style={{ marginLeft: 8, color: "#6366f1", fontWeight: 600 }}>{urlCount} URL{urlCount !== 1 ? "s" : ""}</span>}
                    </label>
                    <textarea
                      value={urlsText}
                      onChange={e => setUrlsText(e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      rows={5}
                      style={{ width: "100%", fontFamily: "monospace", fontSize: "12px", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", resize: "vertical", boxSizing: "border-box" }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Label prefix (optional)</label>
                    <input type="text" value={globalLabel} onChange={e => setGlobalLabel(e.target.value)} placeholder="e.g. Aayurja SEO 2026" />
                  </div>
                  <button className="btn btn-primary" disabled={loadingLinks || urlCount === 0} style={{ width: "100%" }}>
                    {loadingLinks ? "Ingesting " + urlCount + " URL" + (urlCount !== 1 ? "s" : "") + "…" : urlCount > 1 ? "▶ Ingest All " + urlCount + " URLs" : "▶ Add to Knowledge Base"}
                  </button>
                </form>
                <p style={{ fontSize: "11px", color: "#6b7280", marginTop: "10px" }}>Google Docs/Sheets must be shared as "Anyone with the link can view". Max 20 URLs per batch.</p>
              </div>

              <div className="admin-card">
                <h3><span>📝</span> Add Note <span style={{ fontSize: "12px", fontWeight: 400, color: "#6b7280" }}>(free-text knowledge)</span></h3>
                <form onSubmit={handleSaveNote}>
                  <div className="form-group">
                    <label>Label</label>
                    <input
                      type="text"
                      value={noteLabel}
                      onChange={e => setNoteLabel(e.target.value)}
                      placeholder="e.g. Internal SEO Checklist"
                    />
                  </div>
                  <div className="form-group">
                    <label>Note content</label>
                    <textarea
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      placeholder="Paste or type any knowledge Guru should know…"
                      rows={6}
                      style={{ width: "100%", padding: "8px", border: "1px solid #d1d5db", borderRadius: "6px", resize: "vertical", boxSizing: "border-box", fontSize: "13px" }}
                    />
                  </div>
                  <button className="btn btn-primary" disabled={savingNote || !noteText.trim()} style={{ width: "100%" }}>
                    {savingNote ? "⏳ Saving…" : "💾 Add to Knowledge Base"}
                  </button>
                </form>
              </div>
            </div>

            {batchResults && (
              <div style={{ marginTop: "16px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "16px" }}>
                <div style={{ display: "flex", gap: "16px", marginBottom: "12px", flexWrap: "wrap" }}>
                  <span style={{ color: "#16a34a", fontWeight: 600 }}>✅ {batchResults.summary.success} succeeded</span>
                  {batchResults.summary.failed > 0 && <span style={{ color: "#dc2626", fontWeight: 600 }}>❌ {batchResults.summary.failed} failed</span>}
                  <span style={{ color: "#6b7280" }}>{batchResults.summary.total_chunks_added} chunks added</span>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e5e7eb", textAlign: "left" }}>
                      <th style={{ padding: "6px 8px" }}>URL</th>
                      <th style={{ padding: "6px 8px" }}>Status</th>
                      <th style={{ padding: "6px 8px" }}>Chunks</th>
                      <th style={{ padding: "6px 8px" }}>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchResults.results.map((r, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "6px 8px", maxWidth: "280px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><span title={r.url}>{r.url}</span></td>
                        <td style={{ padding: "6px 8px" }}>
                          {r.status === "ok"
                            ? <span style={{ color: "#16a34a", fontWeight: 600 }}>✅ OK</span>
                            : <span style={{ color: "#dc2626", fontWeight: 600 }}>❌ Failed</span>}
                        </td>
                        <td style={{ padding: "6px 8px" }}>{r.chunks_added != null ? r.chunks_added : "—"}</td>
                        <td style={{ padding: "6px 8px", color: "#6b7280", fontSize: "12px" }}>{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="sources-section">
              <h2>Ingested Sources ({sources.length})</h2>
              {sources.length === 0 ? (
                <div className="empty-sources">No sources yet. Upload a file or paste URLs above to get started.</div>
              ) : (
                <div className="table-scroll">
                <table className="sources-table">
                  <thead>
                    <tr>
                      <th>Label</th><th>Type</th><th>Chunks</th><th>Added</th><th>Last Synced</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map(s => {
                      const canReingest   = !!s.url;
                      const isReingesting = !!reingesting[s.id];
                      const isRetagging   = !!retagging[s.id];
                      const isSensitiving = !!sensitiving[s.id];
                      const isSensitive   = !!srcSensitive[s.id];
                      const form          = retagForm[s.id] || { label: s.label || "", category: "" };
                      return (
                        <React.Fragment key={s.id}>
                          <tr>
                            <td title={s.url || s.filename}>{s.label}</td>
                            <td><span className={"badge " + (TYPE_BADGE[s.type] || "")}>{TYPE_LABEL[s.type] || s.type}</span></td>
                            <td>{s.chunk_count}</td>
                            <td>{new Date(s.added_on).toLocaleDateString()}</td>
                            <td style={{ color: "#6b7280", fontSize: "12px" }}>
                              {s.last_synced ? new Date(s.last_synced).toLocaleString() : <span style={{ color: "#d1d5db" }}>—</span>}
                            </td>
                            <td style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                              {canReingest && (
                                <button className="btn btn-secondary" onClick={() => handleReingest(s.id, s.label)} disabled={isReingesting} style={{ whiteSpace: "nowrap" }}>
                                  {isReingesting ? "⏳ Syncing…" : "🔄 Re-ingest"}
                                </button>
                              )}
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleRetagToggle(s.id, s.label)}
                                title="Retag this source"
                                style={{ padding: "3px 8px" }}
                              >🏷️</button>
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleSensitiveToggle(s.id)}
                                disabled={isSensitiving}
                                title={isSensitive ? "Unmark — make visible to Guru" : "Mark sensitive — hide from Guru"}
                                style={{ padding: "3px 8px" }}
                              >{isSensitiving ? "⏳" : isSensitive ? "🔒" : "👁️"}</button>
                              <button className="btn btn-danger" onClick={() => handleDelete(s.id, s.label)} disabled={isReingesting}>Remove</button>
                            </td>
                          </tr>
                          {retagOpen[s.id] && (
                            <tr>
                              <td colSpan="6" style={{ background: "#f9fafb", padding: "10px 14px", borderBottom: "1px solid #e5e7eb" }}>
                                <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                  <input
                                    type="text"
                                    placeholder="New label"
                                    value={form.label}
                                    onChange={e => setRetagForm(f => ({ ...f, [s.id]: { ...form, label: e.target.value } }))}
                                    style={{ flex: 1, minWidth: "140px", padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: "5px", fontSize: "13px" }}
                                  />
                                  <select
                                    value={form.category}
                                    onChange={e => setRetagForm(f => ({ ...f, [s.id]: { ...form, category: e.target.value } }))}
                                    style={{ padding: "5px 8px", border: "1px solid #d1d5db", borderRadius: "5px", fontSize: "13px" }}
                                  >
                                    <option value="">— category —</option>
                                    <option value="sop">SOP</option>
                                    <option value="client-data">Client Data</option>
                                    <option value="seo-guide">SEO Guide</option>
                                    <option value="rankings">Rankings</option>
                                    <option value="onboarding">Onboarding</option>
                                  </select>
                                  <button className="btn btn-primary" onClick={() => handleRetagSave(s.id)} disabled={isRetagging} style={{ padding: "5px 14px" }}>
                                    {isRetagging ? "⏳" : "Save"}
                                  </button>
                                  <button className="btn btn-secondary" onClick={() => setRetagOpen(r => ({ ...r, [s.id]: false }))} style={{ padding: "5px 10px" }}>
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
